/**
 * Versioned storage schema v1 — see contracts/storage-schema.md.
 *
 * `load()` validates shape, drops invalid items (partial recovery is
 * preferred over losing the visitor's whole channel), and clamps
 * currentIndex. `migrate()` is a placeholder for the future — v1 is
 * the only version today.
 */

import type { Item, LibraryBlobV1, Queue } from "../../types.ts";

const STORAGE_KEY = "night-light.library.v1";

function isFiniteNumber(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

function isPositiveInt(x: unknown): x is number {
  return typeof x === "number" && Number.isFinite(x) && x > 0 && Math.floor(x) === x;
}

function validateItem(raw: unknown): Item | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.identifier !== "string" || !/^[a-zA-Z0-9._-]+$/.test(r.identifier)) return null;
  if (typeof r.title !== "string") return null;
  if (typeof r.streamUrl !== "string") return null;
  try {
    const u = new URL(r.streamUrl);
    const host = u.hostname.toLowerCase();
    if (host !== "archive.org" && !host.endsWith(".archive.org")) return null;
  } catch {
    return null;
  }
  if (typeof r.detailsUrl !== "string") return null;
  if (!isFiniteNumber(r.displayAspectRatio) || r.displayAspectRatio <= 0) return null;
  if (typeof r.isEmbedFallback !== "boolean") return null;

  return {
    identifier: r.identifier,
    title: r.title,
    year: typeof r.year === "string" ? r.year : undefined,
    uploader: typeof r.uploader === "string" ? r.uploader : undefined,
    license:
      r.license && typeof r.license === "object"
        ? (r.license as Item["license"])
        : undefined,
    durationSeconds: isFiniteNumber(r.durationSeconds) ? r.durationSeconds : 0,
    thumbUrl: typeof r.thumbUrl === "string" ? r.thumbUrl : "",
    streamUrl: r.streamUrl,
    encodedWidth: isPositiveInt(r.encodedWidth) ? r.encodedWidth : 0,
    encodedHeight: isPositiveInt(r.encodedHeight) ? r.encodedHeight : 0,
    displayAspectRatio: r.displayAspectRatio,
    detailsUrl: r.detailsUrl,
    isEmbedFallback: r.isEmbedFallback,
  };
}

function validateQueue(raw: unknown): Queue | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.name !== "string") return null;
  if (!Array.isArray(r.items)) return null;

  const items: Item[] = [];
  for (const rawItem of r.items) {
    const it = validateItem(rawItem);
    if (it) items.push(it);
  }

  let currentIndex = isFiniteNumber(r.currentIndex) ? Math.floor(r.currentIndex) : -1;
  if (items.length === 0) currentIndex = -1;
  else {
    if (currentIndex < 0) currentIndex = 0;
    if (currentIndex > items.length - 1) currentIndex = items.length - 1;
  }

  return { name: r.name, items, currentIndex };
}

export function migrate(raw: unknown): LibraryBlobV1 | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (r.version !== 1) return null;
  const queue = validateQueue(r.queue);
  if (!queue) return null;
  const settings = (r.settings && typeof r.settings === "object"
    ? (r.settings as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  return {
    version: 1,
    queue,
    settings: {
      autoplayUnlocked: settings.autoplayUnlocked === true,
      userOwnsQueue: settings.userOwnsQueue === true,
    },
    updatedAt: isFiniteNumber(r.updatedAt) ? r.updatedAt : Date.now(),
  };
}

export function loadBlob(): LibraryBlobV1 | null {
  if (typeof localStorage === "undefined") return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  return migrate(parsed);
}

export function saveBlob(blob: LibraryBlobV1): void {
  if (typeof localStorage === "undefined") return;
  try {
    const serialized = JSON.stringify({ ...blob, updatedAt: Date.now() });
    localStorage.setItem(STORAGE_KEY, serialized);
  } catch {
    // QuotaExceededError or similar — silently no-op per contracts/storage-schema.md.
  }
}

export function clearBlob(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
